import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, IconButton, Stack, TextField, Typography } from '@mui/material';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useState } from 'react';

import type { IAlertNote } from 'common/src/types/interfaces';

import { abbreviateName } from '../utils/abbreviateName';

const AlertNoteItem = ({
  canModify,
  isEditingDisabled,
  isSaving,
  note,
  onDelete,
  onEditingChange,
  onUpdate,
}: {
  canModify: boolean;
  isEditingDisabled: boolean;
  isSaving: boolean;
  note: IAlertNote;
  onDelete: (noteId: number) => void;
  onEditingChange: (isEditing: boolean) => void;
  onUpdate: (noteId: number, note: string) => Promise<unknown>;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(note.note);

  const handleCancel = () => {
    setDraft(note.note);
    setIsEditing(false);
    onEditingChange(false);
  };

  const handleSave = async () => {
    await onUpdate(note.id, draft.trim());
    setIsEditing(false);
    onEditingChange(false);
  };

  const isSaveDisabled =
    isSaving || draft.trim() === note.note || draft.trim() === '';

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        pb: 0,
      }}
    >
      {isEditing ? (
        <Stack
          display="flex"
          alignItems="center"
          direction="row"
          spacing={1}
          pb={1}
        >
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            slotProps={{ input: { sx: { fontSize: 14, px: 1, py: 0.5 } } }}
          />
          <IconButton
            aria-label="Save note edit"
            size="small"
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="Cancel note edit"
            size="small"
            onClick={handleCancel}
            disabled={isSaving}
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) : (
        <>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {note.note}
          </Typography>
          <Box
            display="flex"
            alignItems="flex-end"
            justifyContent="space-between"
            gap={1}
            sx={{ my: 0.3 }}
          >
            <Typography variant="caption" color="text.secondary">
              {abbreviateName(note.createdByUser?.name)} ·{' '}
              {formatDistanceToNow(parseISO(note.createdAt), {
                addSuffix: true,
              })}
              {note.updatedBy ? ' (edited)' : ''}
            </Typography>
            {canModify && (
              <Stack direction="row" spacing={0.5}>
                <IconButton
                  aria-label="Edit note"
                  size="small"
                  onClick={() => {
                    setIsEditing(true);
                    onEditingChange(true);
                  }}
                  disabled={isEditingDisabled}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Delete note"
                  size="small"
                  onClick={() => onDelete(note.id)}
                  disabled={isEditingDisabled}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default AlertNoteItem;
